import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { CustomFormContext } from '../../../customFormContextProvider';
import Slider from 'react-slick';
import DictionaryTerm from '../../../../../@shared/dictionaryTerm';
import Card from './card';
import { TCardCarouselProps, TCardProps } from '../../../../types/dealerLocator.types';
import useMediaQuery from '../../../../../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../../../../../constants/breakpoints.constants';
import CarouselArrow from '../../../../../@shared/carouselArrow';
import CustomCarouselDots from '../../../../../@shared/customCarouselDots';
import Icon from '../../../../../@shared/icon';
import { iconSize } from '../../../../../../constants/icon.constants';
import { debounceHandler } from '../../../../../../utils/debounceHandler.utils';
import './styles.scss';

const CardCarousel: FC<TCardCarouselProps> = (props: TCardCarouselProps) => {
  const { cards, distanceUnit } = props;
  const cardsLength = cards.length;
  const cardWidthRef = useRef<HTMLDivElement | null>(null);
  const timmerRef = useRef<ReturnType<typeof setTimeout>>();
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-p']})`, true);
  const [activeCard, updateActiveCardNumber] = useState<number>(1); // eslint-disable-line
  const [shouldShowMask, setShouldShowMask] = useState<boolean>(false);
  const [showLeftMask, setShowLeftMask] = useState<boolean>(false);
  const [showRightMask, setShowRightMask] = useState<boolean>(false);
  const [selectedDealerNumber, setSelectedDealerNumber] = useState(props?.value || '');
  const customFormContext = useContext(CustomFormContext);

  const checkContainerWidthForMask = (containerWidth: number) => {
    const _DESKTOP_CARD_WIDTH = cardsLength < 3 ? 657 : 392;
    const _MOBILE_CARD_WIDTH = 300;
    const _cardContainerWidth = Math.min(containerWidth || 1680);
    const _cardWidth = isDesktop ? _DESKTOP_CARD_WIDTH : _MOBILE_CARD_WIDTH;
    const totalWidth = _cardWidth * cardsLength;
    setShouldShowMask(totalWidth > _cardContainerWidth);
  };

  useEffect(() => {
    checkContainerWidthForMask(cardWidthRef?.current?.clientWidth ?? 0);
    const observer = new ResizeObserver((entries) => {
      debounceHandler(() => checkContainerWidthForMask(entries[0].contentRect.width), timmerRef, 100);
    });
    if (cardWidthRef?.current) {
      observer.observe(cardWidthRef.current as any);
    }
    return () => {
      cardWidthRef.current && observer.unobserve(cardWidthRef.current);
    };
  }, []);

  useEffect(() => {
    setShowRightMask(shouldShowMask && activeCard !== cardsLength);
    setShowLeftMask(shouldShowMask && activeCard !== 1);
  }, [activeCard, shouldShowMask]);

  const updateSelectedDealerNumberAndName = (dealerNumber: string, dealerName: string) => {
    setSelectedDealerNumber(dealerNumber);
    customFormContext?.setSelectedDealerName?.(dealerName);
    props.updateSelectedDealerNumber(dealerNumber);
  };

  const cardsMapper = (items: TCardProps[]): JSX.Element[] => {
    const isLarge = items.length < 3;
    const cardsArray = items.map((item) => (
      <Card
        {...item}
        selectedDealerNumber={selectedDealerNumber}
        updateSelectedDealerNumberAndName={updateSelectedDealerNumberAndName}
        isLarge={isLarge}
        distanceUnit={distanceUnit}
        key={item?.dealerNumber}
      />
    ));

    return cardsArray;
  };

  const settings = {
    arrows: true,
    infinite: false,
    variableWidth: true,
    slidesToScroll: 1,
    slidesToShow: 1,
    dots: true,
    nextArrow: (
      <CarouselArrow
        direction={'next'}
        additionalCssClass={`preferred-dealer-carousel__arrow ${
          activeCard === cardsLength ? 'preferred-dealer-carousel__arrow--hidden' : ''
        }`}
      />
    ) as any,
    prevArrow: (
      <CarouselArrow
        direction={'previous'}
        additionalCssClass={`preferred-dealer-carousel__arrow ${activeCard === 1 ? 'preferred-dealer-carousel__arrow--hidden' : ''}`}
      />
    ) as any,
    dotsClass: 'preferred-dealer-card__dots',
    afterChange: (newIndex: number) => {
      updateActiveCardNumber(newIndex + 1);
    },
    appendDots: () => <CustomCarouselDots cardsCount={cardsLength} activeCard={activeCard} />, // eslint-disable-line
  };

  if (cardsLength === 0) {
    return (
      <div className="preferred-dealer-carousel__search-no-match">
        <Icon iconName="info-alert-notif-info" size={iconSize.xlg} extraClassName="dealer-info-icon" />
        <p className="preferred-dealer-carousel__search-no-match-title">
          <DictionaryTerm keyword={'cnhi-formsDealersNoMatchesFoundTitle'} />
        </p>
        <p className="preferred-dealer-carousel__search-no-match-desc">
          <DictionaryTerm keyword={'cnhi-formsDealersNoMatchesFoundDescription'} />
        </p>
      </div>
    );
  }

  return (
    <div className="preferred-dealer-carousel" ref={cardWidthRef}>
      {showLeftMask && <div className="preferred-dealer-carousel__left-mask"></div>}
      {showRightMask && <div className="preferred-dealer-carousel__right-mask"></div>}
      <p className="preferred-dealer-carousel__title">
        {cards.length} <DictionaryTerm keyword={'cnhi-formsDealersCardsCount'} />
      </p>
      <Slider {...settings}>{cardsMapper(cards)}</Slider>
    </div>
  );
};

export default CardCarousel;
